import { useCallback, useMemo } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { SystemSettingKey, systemSettingsApi } from "@/api";
import { QueryKey } from "@/query/Query";

import {
  FolderSelectionOutcome,
  FolderSelectionResult,
  MailMergeAccessState,
  MailMergeWriteFailure,
  MailMergeWriteResult,
} from "../MailMergeAccess.types";
import { folderLeafOf, folderNamesMatch, resolveAccessState } from "../MailMergeAccess.utils";
import {
  FileContent,
  isFileSystemAccessSupported,
  pickFolder,
  queryFolderAccess,
  requestFolderAccess,
  writeFileToFolder,
} from "../utils/fileSystemAccess";
import {
  clearStoredFolderHandle,
  getStoredFolderHandle,
  storeFolderHandle,
} from "../utils/mailMergeFolderHandleStorage";

const SETTING_KEY = SystemSettingKey.MailMergeDataSourceFolder;

const settingQueryOptions = {
  queryKey: [QueryKey.SystemSetting, SETTING_KEY],
  queryFn: () => systemSettingsApi.getSystemSetting({ key: SETTING_KEY }),
};

export interface ResolvedGrant {
  state: MailMergeAccessState;
  handle: FileSystemDirectoryHandle | null;
  /** The configured path, as the Supervisor entered it — what the user is told to look for. */
  expectedFolderPath: string | null;
  /** The folder that path ends in — the only part a released folder can be matched against. */
  expectedFolderLeaf: string | null;
}

/**
 * Owns the browser-local grant to the mail merge (Serienbrief) data source folder: what state it is
 * in, how it is established, restored and dropped, and how a file is written into it. The dialogs
 * that must precede a browser prompt live one level up, in useMailMergeAccessPrompts — a feature
 * gates its flow on that hook's requestAccess rather than calling in here directly.
 *
 * The returned `state` is for display. Anything that acts on the grant resolves it first through
 * {@link readCurrentGrant}, because a click both establishes access and uses it, and the rendered
 * state is a snapshot from before that click. {@link writeFile} does this itself and never prompts,
 * so it may run after any amount of asynchronous work.
 */
export function useMailMergeAccess() {
  const queryClient = useQueryClient();
  const isSupported = isFileSystemAccessSupported();

  const { data: setting, isLoading: isSettingLoading, isError: isSettingError } = useQuery(settingQueryOptions);

  const expectedFolderPath = toFolderPath(setting?.value);
  const expectedFolderLeaf = expectedFolderPath === null ? null : folderLeafOf(expectedFolderPath);

  // The grant lives in the browser, so it is re-read rather than remembered: it can end between two
  // page views without the application taking part.
  const {
    data: grant,
    isLoading: isGrantLoading,
    isError: isGrantError,
  } = useQuery({
    queryKey: [QueryKey.MailMergeFolderGrant],
    queryFn: async () => {
      const handle = await getStoredFolderHandle();
      return { handle, permission: handle ? await queryFolderAccess(handle) : null };
    },
    enabled: isSupported,
  });

  const handle = grant?.handle ?? null;

  const state = useMemo(
    () =>
      resolveAccessState({
        isSupported,
        expectedFolderLeaf,
        handle,
        permission: grant?.permission ?? null,
      }),
    [isSupported, expectedFolderLeaf, handle, grant?.permission]
  );

  const invalidateGrant = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [QueryKey.MailMergeFolderGrant] }),
    [queryClient]
  );

  /**
   * Reads the grant out of the browser rather than out of this render. Anything that acts on the
   * state — rather than displaying it — has to go through here: a caller establishes access and
   * writes within one click, so the rendered `state` it closed over is a snapshot from before the
   * grant it just obtained, and on the first interaction after a page load the queries may not have
   * resolved at all.
   *
   * The configured path is taken from the cache and not re-fetched, so one interaction sees one
   * consistent value and pays no round trip. Picking a rename up is the mount-time query's job.
   */
  const readCurrentGrant = useCallback(async (): Promise<ResolvedGrant> => {
    if (!isSupported) {
      return {
        state: MailMergeAccessState.Unsupported,
        handle: null,
        expectedFolderPath: null,
        expectedFolderLeaf: null,
      };
    }

    const currentSetting = await queryClient.query({ ...settingQueryOptions, staleTime: "static" });
    const currentPath = toFolderPath(currentSetting.value);
    const currentLeaf = currentPath === null ? null : folderLeafOf(currentPath);
    const currentHandle = await getStoredFolderHandle();
    const permission = currentHandle ? await queryFolderAccess(currentHandle) : null;

    return {
      state: resolveAccessState({
        isSupported,
        expectedFolderLeaf: currentLeaf,
        handle: currentHandle,
        permission,
      }),
      handle: currentHandle,
      expectedFolderPath: currentPath,
      expectedFolderLeaf: currentLeaf,
    };
  }, [isSupported, queryClient]);

  /** Takes the expected leaf from the grant the caller already resolved for this click. */
  const chooseFolder = useCallback(
    async (expectedLeaf: string | null): Promise<FolderSelectionResult> => {
      const picked = await pickFolder();

      if (!picked) {
        return { outcome: FolderSelectionOutcome.Cancelled };
      }

      if (!expectedLeaf || !folderNamesMatch(picked.name, expectedLeaf)) {
        // Not stored: a grant to the wrong folder writes successfully and fails invisibly in Word.
        return { outcome: FolderSelectionOutcome.NameMismatch, selectedFolderName: picked.name };
      }

      await storeFolderHandle(picked);
      await invalidateGrant();

      return { outcome: FolderSelectionOutcome.Granted };
    },
    [invalidateGrant]
  );

  /**
   * Restores a permission the browser withdrew. Raises the permission bar, so it must be reached
   * straight from a click: pass a grant already resolved for that click rather than letting this
   * re-read one, or the read spends the activation the bar needs.
   */
  const ensureAccess = useCallback(
    async (resolved: ResolvedGrant): Promise<MailMergeAccessState> => {
      if (resolved.state !== MailMergeAccessState.PermissionLost || !resolved.handle) {
        return resolved.state;
      }

      await requestFolderAccess(resolved.handle);
      await invalidateGrant();

      return (await readCurrentGrant()).state;
    },
    [readCurrentGrant, invalidateGrant]
  );

  const resetAccess = useCallback(async (): Promise<void> => {
    await clearStoredFolderHandle();
    await invalidateGrant();
  }, [invalidateGrant]);

  const writeFile = useCallback(
    async (fileName: string, content: FileContent): Promise<MailMergeWriteResult> => {
      // The result type promises failures are reported rather than raised, so the whole body sits
      // inside one catch: a caller that cannot reach its folder is in no better position than one
      // whose write was refused, and neither should have to handle a rejection.
      try {
        const current = await readCurrentGrant();

        if (current.state !== MailMergeAccessState.Ready || !current.handle) {
          // The rendered state said otherwise, or it would not have been offered — bring the display
          // back in line so the card does not keep claiming the folder is released.
          await invalidateGrant();
          return { success: false, failure: MailMergeWriteFailure.NotReady };
        }

        try {
          await writeFileToFolder(current.handle, fileName, content);
          return { success: true };
        } catch (error) {
          return await classifyWriteFailure(current.handle, error, invalidateGrant);
        }
      } catch {
        return { success: false, failure: MailMergeWriteFailure.WriteFailed };
      }
    },
    [readCurrentGrant, invalidateGrant]
  );

  return {
    state,
    isLoading: isSettingLoading || isGrantLoading,
    isError: isSettingError || isGrantError,
    expectedFolderPath,
    readCurrentGrant,
    chooseFolder,
    ensureAccess,
    resetAccess,
    writeFile,
  };
}

// Trimmed, not just checked for empty: the backend rejects a path with surrounding whitespace, so a
// stored value made of it is the "no folder configured" case rather than a folder anyone can release.
function toFolderPath(value: unknown): string | null {
  const path = typeof value === "string" ? value.trim() : "";
  return path === "" ? null : path;
}

async function classifyWriteFailure(
  handle: FileSystemDirectoryHandle,
  error: unknown,
  invalidateGrant: () => Promise<void>
): Promise<MailMergeWriteResult> {
  // Chrome reports a file Word holds open and a withdrawn grant with overlapping error names,
  // so the permission itself is what decides which of the two the user is told about.
  if ((await queryFolderAccess(handle)) !== "granted") {
    await invalidateGrant();
    return { success: false, failure: MailMergeWriteFailure.PermissionLost };
  }

  if (error instanceof DOMException && error.name === "NoModificationAllowedError") {
    return { success: false, failure: MailMergeWriteFailure.FileLocked };
  }

  return { success: false, failure: MailMergeWriteFailure.WriteFailed };
}
